import React, { useEffect, useRef, useState } from "react";
import AddButton from "../buttons/button";
import ItemsInputField from "../forms/items-input-field";
import ItemsSelectField from "../forms/items-select-field";
import UploadIcon from "../../assets/icons/upload-icon";
import ViewIcon from "../../assets/icons/view-icon";
import { useFormikContext } from "formik";
import { ChevronUp, ChevronDown } from "lucide-react";
import { handleShowErrorToast } from "../helper-components/helper-function";
import { useNavigate } from "react-router-dom";
import { api } from "../../redux/apis/api";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const ReturnBillFooterSection = ({
  docList = [],
  disabled,
  page = "create",
  returnCreateInputs = [],
  icn_id_from
}) => {
  const header_inv_dis_per_ref = useRef();
  const { values, setFieldValue, errors, validateForm } = useFormikContext();
  console.log(values, "dsfdsfsdfsdfsdfsdf");
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  // ---- Benefit adjustment ------------------------------------------------
  // The originating bill's scheme/coupon/points discounts are bill-level,
  // so a refund computed from gross line totals over-refunds. Pro-rate them
  // over the returned lines:
  //   ratio      = benefitTotal / GROSS LINE TOTAL (sot_sub_total)
  //   adjustment = Σ returned item_total_amount × ratio(line's bill)
  // The denominator MUST be the gross line total because the numerator base
  // (returned line totals) is gross — net+benefit understates it for bills
  // with a manual invoice discount, overstates the adjustment, and the
  // backend guard uses the same gross-lines formula. Falls back to
  // net+benefit only when sub_total is unavailable (legacy rows).
  // ADD mode rows carry the originating sales_order figures directly (the
  // items GET joins sales_order.*); rows without them (edit of a saved
  // draft) fall back to GET /order/bill-benefits/:sot_id, cached per bill.
  const [fetchedBillBenefits, setFetchedBillBenefits] = useState({});

  const getBillBenefitFigures = (row) => {
    if (row?.sot_total_amount != null) {
      const benefitTotal =
        (parseFloat(row?.sot_scheme_discount) || 0) +
        (parseFloat(row?.sot_coupon_discount) || 0) +
        (parseFloat(row?.sot_points_discount) || 0);
      return {
        benefitTotal,
        net: parseFloat(row.sot_total_amount) || 0,
        grossLines: parseFloat(row?.sot_sub_total) || 0,
      };
    }
    return (row?.sot_id && fetchedBillBenefits[row.sot_id]) || null;
  };

  useEffect(() => {
    if (disabled) return;
    const missing = [
      ...new Set(
        (values?.data || [])
          .filter((row) => row?.sot_id && row?.sot_total_amount == null)
          .map((row) => row.sot_id)
      ),
    ].filter((sotId) => fetchedBillBenefits[sotId] === undefined);
    if (missing.length === 0) return;
    let cancelled = false;
    missing.forEach((sotId) => {
      api
        .get(`/order/bill-benefits/${sotId}`)
        .then((res) => {
          if (cancelled) return;
          const d = res?.data?.data || {};
          const benefitTotal =
            (Number(d?.scheme?.amount) || 0) +
            (Number(d?.coupon?.amount) || 0) +
            (Number(d?.points?.amount) || 0);
          setFetchedBillBenefits((prev) => ({
            ...prev,
            [sotId]: {
              benefitTotal,
              net: Number(d?.totals?.net) || 0,
              grossLines: Number(d?.totals?.gross_items) || 0,
            },
          }));
        })
        .catch(() => {
          if (cancelled) return;
          // No data → treat as a no-benefit bill; refund math is unchanged.
          setFetchedBillBenefits((prev) => ({
            ...prev,
            [sotId]: { benefitTotal: 0, net: 0 },
          }));
        });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.data, disabled, fetchedBillBenefits]);

  const computeBenefitAdjustment = (rows) =>
    round2(
      (rows || []).reduce((sum, row) => {
        const fig = getBillBenefitFigures(row);
        if (!fig || fig.benefitTotal <= 0) return sum;
        const denominator =
          fig.grossLines > 0 ? fig.grossLines : fig.net + fig.benefitTotal;
        if (denominator <= 0) return sum;
        return (
          sum +
          (parseFloat(row?.item_total_amount) || 0) *
            (fig.benefitTotal / denominator)
        );
      }, 0)
    );

  // Keep icn_total_amount net of the pro-rated benefit adjustment. Runs
  // after the items-section handlers write their gross totals (they only
  // know line math). When the originating bill(s) had no benefits the
  // adjustment is 0 and NOTHING is touched — identical to today's flow.
  useEffect(() => {
    if (disabled) return; // view mode renders the stored figures only
    const rows = values?.data || [];
    // Don't recompute while an originating bill's figures are still being
    // fetched — a transient 0 would wipe a draft's stored adjustment.
    const pending = rows.some(
      (row) =>
        row?.sot_id &&
        row?.sot_total_amount == null &&
        fetchedBillBenefits[row.sot_id] === undefined
    );
    if (pending) return;
    const adjustment = computeBenefitAdjustment(rows);
    const current = parseFloat(values?.icn_benefit_adjustment || 0) || 0;
    if (adjustment <= 0) {
      if (current !== 0) setFieldValue("icn_benefit_adjustment", 0);
      return;
    }
    const gross = rows.reduce(
      (total, item) => total + parseFloat(item?.item_total_amount || 0),
      0
    );
    const invoiceDiscount = parseFloat(values?.icn_invoice_discount || 0) || 0;
    const net = gross - invoiceDiscount - adjustment;
    const rounded_total = Math.round(net);
    if (
      current !== adjustment ||
      parseFloat(values?.icn_total_amount || 0) !== rounded_total
    ) {
      setFieldValue("icn_benefit_adjustment", adjustment);
      setFieldValue("icn_total_amount", rounded_total.toFixed(2));
      setFieldValue("icn_round_off_value", (rounded_total - net).toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values?.data,
    values?.icn_invoice_discount,
    fetchedBillBenefits,
    disabled,
  ]);

  const onChangeHeaderDiscount = (
    header_inv_dis_per,
    icn_is_invoice_dis_percentage
  ) => {
    let updatedData = [...values?.data];

    // Update totals
    const icn_total_gst = updatedData.reduce(
      (total, item) => total + parseFloat(item.item_tax_amount || 0),
      0
    );
    const icn_total_discount = updatedData.reduce(
      (total, item) => total + parseFloat(item.item_discount_amount || 0),
      0
    );
    const icn_sub_total = updatedData.reduce(
      (subtotal, item) =>
        subtotal +
        parseFloat(item?.item_unit_price || 0) *
        parseFloat(item?.quantity || 0),
      0
    );

    const icn_total_without_inv_dis = updatedData.reduce(
      (total, item) => total + parseFloat(item.item_total_amount || 0),
      0
    );

    const icn_invoice_discount = icn_is_invoice_dis_percentage
      ? (icn_total_without_inv_dis * header_inv_dis_per) / 100
      : header_inv_dis_per;

    const icn_invoice_discount_percentage = icn_is_invoice_dis_percentage
      ? header_inv_dis_per
      : (header_inv_dis_per / icn_total_without_inv_dis) * 100;

    // The pro-rated benefit adjustment depends only on the lines (× ratio),
    // not on the invoice discount, so the stored value is still valid here.
    const icn_total_amount =
      updatedData.reduce(
        (total, item) => total + parseFloat(item.item_total_amount || 0),
        0
      ) -
      parseFloat(icn_invoice_discount) -
      (parseFloat(values?.icn_benefit_adjustment || 0) || 0);
    const rounded_total = Math.round(icn_total_amount);
    const roundoff = (rounded_total - icn_total_amount).toFixed(2);
    setFieldValue("icn_total_gst", icn_total_gst.toFixed(2));
    setFieldValue("icn_total_discount", icn_total_discount.toFixed(2));
    setFieldValue("icn_sub_total", icn_sub_total.toFixed(2));
    setFieldValue("icn_total_amount", rounded_total.toFixed(2));
    setFieldValue("icn_round_off_value", roundoff);
    setFieldValue("header_dis_per", "");
    setFieldValue("icn_invoice_discount", icn_invoice_discount?.toFixed(2));
    setFieldValue(
      "icn_invoice_discount_percentage",
      icn_invoice_discount_percentage
    );
  };

  return (
    <div className="h-[8rem] bg-white relative z-50">
      <div className="h-[4rem] border-y border-[var(--create-table-footer-border)] flex items-center justify-between">
        <div className="flex w-[80%] h-full">
          {returnCreateInputs?.find((inp) => inp?.value === "remarks")
            ?.enabled && (
              <div className="w-[50%] px-5 border-r border-r-[var(--create-table-footer-border)] h-full flex items-center justify-center">
                <div className="w-[80%]">
                  {" "}
                  <ItemsInputField
                    disabled={false}
                    name={`icn_remarks`}
                    isLabelReq={false}
                    placeholder="Enter your remarks here..."
                    allowAll={true}
                  />
                </div>
              </div>
            )}

          {returnCreateInputs?.find(
            (inp) => inp?.value === "header_inv_dis_per"
          )?.enabled && (
              <div className="w-[50%] px-5 border-r border-r-[var(--create-table-footer-border)] h-full flex gap-3 items-center justify-center">
                {returnCreateInputs?.find(
                  (inp) => inp?.value === "header_inv_dis_per"
                )?.enabled && (
                    <div className={`w-[60%]  relative`}>
                      <ItemsInputField
                        ref={header_inv_dis_per_ref}
                        disabled={true}
                        name={`header_inv_dis_per`}
                        isLabelReq={false}
                        placeholder={`Inv Disc ${values?.icn_is_invoice_dis_percentage ? "%" : "₹"
                          }`}
                        onKeyDown={(event) => {
                          if (event.key === "Tab" || event.key === "Enter") {
                            onChangeHeaderDiscount(
                              parseFloat(event.target.value || 0),
                              values?.icn_is_invoice_dis_percentage
                            );
                          }
                        }}
                        onBlur={(event) => {
                          onChangeHeaderDiscount(
                            parseFloat(event.target.value || 0),
                            values?.icn_is_invoice_dis_percentage
                          );
                        }}
                      />
                      <button
                        disabled={true}
                        onClick={() => {
                          setFieldValue(
                            "icn_is_invoice_dis_percentage",
                            !values?.icn_is_invoice_dis_percentage
                          );
                          onChangeHeaderDiscount(
                            parseFloat(values?.header_inv_dis_per),
                            !values?.icn_is_invoice_dis_percentage
                          );
                        }}
                        type="button"
                        className="absolute top-1/2 -translate-y-1/2 right-0 w-[40px] min-h-[40px] flex items-center justify-center bg-[var(--create-tab-foot-subTot-bg)] border border-[var(--items-search-input-border)] rounded-r"
                      >
                        {values?.icn_is_invoice_dis_percentage ? "%" : "₹"}
                      </button>
                    </div>
                  )}
              </div>
            )}
        </div>

        <div
          className="justify-between w-[20%] px-5 h-full flex flex-col items-center
      bg-[var(--create-table-footer-totals-bg)] text-[var(--create-table-footer-totals-fg)]
      text-[length:var(--create-table-footer-totals-fs)] font-[var(--create-table-footer-totals-fw)] relative"
        >
          {/* Toggle Button */}
          {!isOpen && (
            <div
              className="absolute top-[-1.2rem] left-1/2 transform -translate-x-1/2 bg-[var(--create-table-footer-totals-bg)] cursor-pointer 
                    flex items-start justify-center"
              style={{
                width: "80px",
                height: "55px",
                borderRadius: "9999px", // makes it oval
                padding: "0.2rem",
              }}
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </div>
          )}
          {isOpen && (
            <div
              className="p-5 flex flex-col w-full space-y-5 absolute bottom-[100%]"
              style={{
                backgroundColor: "var(--create-tab-foot-subTot-bg)",
                color: "var(--create-tab-foot-subTot-fg)",
                fontSize: "var(--create-tab-foot-subTot-fs)",
                fontWeight: "var(--create-tab-foot-subTot-fw)",
              }}
            >
              <div className="flex justify-between">
                <span>Sub Total</span>
                <span>₹{values?.icn_sub_total || "0.00"}</span>
              </div>
              <div className="flex justify-between">
                <span>Discount</span>
                <span style={{ color: "var(--success-color)" }}>
                  {"- "}₹{values?.icn_total_discount || "0.00"}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Invoice Discount</span>
                <span style={{ color: "var(--success-color)" }}>
                  {"- "}₹{values?.icn_invoice_discount || "0.00"}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Round off</span>
                <span
                  className={
                    parseFloat(values?.icn_round_off_value || 0) > 0
                      ? "text-[var(--error-color)]"
                      : parseFloat(values?.icn_round_off_value || 0) < 0
                        ? "text-[var(--success-color)]"
                        : ""
                  }
                >
                  ₹{parseFloat(values?.icn_round_off_value || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Tax</span>
                <span style={{ color: "var(--error-color)" }}>
                  ₹{values?.icn_total_gst || "0.00"}
                </span>
              </div>
              {parseFloat(values?.icn_benefit_adjustment || 0) > 0 && (
                <div
                  className="flex justify-between"
                  title="Scheme/coupon/points discounts from the original bill, pro-rated over the returned items"
                >
                  <span>Benefit adjustment</span>
                  <span style={{ color: "var(--success-color)" }}>
                    {"- "}₹
                    {parseFloat(values?.icn_benefit_adjustment || 0).toFixed(2)}
                  </span>
                </div>
              )}
              <div
                className={`absolute top-[-2.3rem] left-1/2 transform -translate-x-1/2 bg-[var(--create-tab-foot-subTot-bg)] cursor-pointer 
              flex items-start justify-center `}
                style={{
                  width: "80px",
                  height: "55px",
                  borderRadius: "9999px", // makes it oval
                  padding: "0.2rem",
                }}
                onClick={() => setIsOpen(!isOpen)}
              >
                {isOpen ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
              </div>
            </div>
          )}

          {/* Total Amount */}
          <div className="flex justify-between w-full h-full items-center">
            <span>Total</span>
            <span>₹{values?.icn_total_amount || "0.00"}</span>
          </div>
        </div>
      </div>

      <div className="h-[4rem] flex justify-between px-5">
        <div></div>
        <div className="flex gap-3 items-center">
          {!disabled && (
            <AddButton
              prefixIcon={false}
              text={"Draft"}
              className="
flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--draft-button-bg)] cursor-pointer
text-[var(--draft-button-fg)]
border border-[var(--draft-button-border)]
text-[length:var(--draft-button-fs)] 
font-[var(--draft-button-fw)]
"
              type="submit"
              onclkFn={() => {
                setFieldValue("submittedButton", "draft");
                if (Object.keys(errors).length > 0) {
                  handleShowErrorToast(errors);
                }
              }}
            />
          )}
          <AddButton
            prefixIcon={false}
            text={disabled ? "View Payment Method" : "Proceed to Payment"}
            className="
flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--primary-button-bg-color)] cursor-pointer
text-[var(--primary-button-fg-color)]
border border-[var(--primary-button-bg-color)]
hover:bg-[var(--primary-button-hover-color)]
text-[length:var(--primary-button-create-fs)] 
font-[var(--primary-button-font-weight)]
"
            type="submit"
            onclkFn={async () => {
              setFieldValue("submittedButton", "proceed");

              const formErrors = await validateForm();
              if (Object.keys(formErrors).length > 0) {
                handleShowErrorToast(formErrors);
              }
            }}
          />
          {disabled && values?.icn_order_status === "draft" && (
            <AddButton
              prefixIcon={false}
              text={"EDIT"}
              className="
flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--primary-button-bg-color)] cursor-pointer
text-[var(--primary-button-fg-color)]
border border-[var(--primary-button-bg-color)]
hover:bg-[var(--primary-button-hover-color)]
text-[length:var(--primary-button-font-size)] 
font-[var(--primary-button-font-weight)]
"
              type="button"
              onclkFn={() => {
                navigate(`/edit-return-bill/${values?.icn_id||icn_id_from }`);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReturnBillFooterSection;
